import React from "react";
import "./css/page_error_boundary.scss";

/**
 * Catches a render error on a loan tool page.
 *
 * Without this, ANY unexpected value on a page tears down the whole React tree
 * and the user is left staring at a blank white screen with no explanation —
 * and reloading doesn't help, because the same loan file loads and fails the
 * same way. That is what an imported file with a missing section looked like.
 *
 * The nav bar lives outside this boundary, so the user can still click away to
 * another page instead of being stuck.
 */
export default class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    // Keep the details in the console for support requests.
    console.error("Loan tool page error:", error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    // Navigating to a different page clears the error so the new page renders.
    if (this.state.error && prevProps.pageKey !== this.props.pageKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    return (
      <div className="page-error-boundary">
        <h2>This page couldn't be displayed</h2>
        <p>
          Something in this loan file isn't in a format this page expects, so it
          couldn't finish loading. The rest of your loan file is safe and your
          other pages still work &mdash; use the buttons above to continue.
        </p>
        <p>
          If this loan file came from a MISMO import, a section of the imported
          data is most likely incomplete.
        </p>
        {/* Re-rendering in place just hits the same data and fails again
            instantly (it looked like the button did nothing). A reload picks up
            the newest deployed build and re-reads the loan from the server; the
            unsaved-changes warning still protects any pending edits. */}
        <button
          className="button"
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
        <details>
          <summary>Technical details</summary>
          <pre>{String(this.state.error && this.state.error.message)}</pre>
        </details>
      </div>
    );
  }
}
